import {
  IIssuingLog,
  IssuingRecordActionEnum,
  IssuedStatusEnum,
  BookingTypeEnum,
  IPlanSummaryData,
  TicketSetting,
} from '@/api/types';
import { formatDate } from './index';
import VueI18n, { IVueI18n } from 'vue-i18n';
import { IPutPlanSettingRequest } from '@/api/tickets';

export type PlanAndUnitWithBookingType = {
  activityId: string;
  activityTitle: string;
  planId: string;
  planTitle: string;
  unitId: string;
  unitTitle: string;
  bookingType: BookingTypeEnum;
};

export type TicketType = PlanAndUnitWithBookingType & {
  ticketId: string;
  ticketTitle: string;
};

export const newDefaultTicketSetting = (supplierId: string) =>
  ({
    supplierId: supplierId,
    planId: '',
    unitId: '',
    issuingRules: [],
    issuableFrom: [],
    issuableTo: [],
    title: '',
    titlesForTraveler: [],
    applyingRule: null,
    mogiriSetting: {
      overrideStaffLabel: [],
      checkInCodes: [],
      cautionLabels: [],
    },
    isInactive: false,
    priority: null,
  } as TicketSetting);

export const newDefaultIPutPlanSettingRequest = () =>
  ({
    planId: '',
    issuingRules: [],
    issuableFrom: [],
    issuableTo: [],
    applyingRule: null,
    printSpecs: [],
    uiSetting: {
      bookingDetailBgcolor: '',
    },
    isInactive: false,
    mogiriSetting: {
      bannerUrls: {
        details: '',
        valid: '',
        expired: '',
      },
      localedExtendedInfo: [],
    },
  } as IPutPlanSettingRequest);

export const sortByPlanAndUnit = (
  a: Required<PlanAndUnitWithBookingType>,
  b: Required<PlanAndUnitWithBookingType>
) => {
  return a.activityTitle < b.activityTitle
    ? -1
    : a.activityTitle > b.activityTitle
    ? 1
    : a.planTitle < b.planTitle
    ? -1
    : a.planTitle > b.planTitle
    ? 1
    : a.unitTitle < b.unitTitle
    ? -1
    : a.unitTitle > b.unitTitle
    ? 1
    : (a.bookingType as BookingTypeEnum) === BookingTypeEnum.EACH_TIME
    ? -1
    : (b.bookingType as BookingTypeEnum) === BookingTypeEnum.EACH_TIME
    ? 1
    : 0;
};

export const planToText = (
  $i18n: VueI18n & IVueI18n,
  item?: IPlanSummaryData
) => {
  const suffix =
    item &&
    item.bookingType &&
    (item.bookingType as BookingTypeEnum) === BookingTypeEnum.BULK
      ? ` [${$i18n.t('BookingTypeEnum.BULK')}]`
      : '';
  return item ? `${item.activityTitle} | ${item.planTitle}${suffix}` : '';
};

export const planAndUnitWithBookingTypeToText = (
  $i18n: VueI18n & IVueI18n,
  item: Required<PlanAndUnitWithBookingType>
) => {
  const suffix =
    item.bookingType &&
    (item.bookingType as BookingTypeEnum) === BookingTypeEnum.BULK
      ? ` [${$i18n.t('BookingTypeEnum.BULK')}]`
      : '';
  return `${item.activityTitle} | ${item.planTitle} | ${item.unitTitle}${suffix}`;
};

export const ticketToText = (
  $i18n: VueI18n & IVueI18n,
  item: Required<TicketType>
) => {
  return `${item.planId} | ${planAndUnitWithBookingTypeToText($i18n, item)} | ${
    item.ticketTitle
  }`;
};

export const getParticipantName = (
  firstName: string,
  lastName: string,
  language: string
) => `${firstName} ${lastName}`;
// language === 'en' || false
//   ? `${firstName} ${lastName}`
//   : `${lastName} ${firstName}`;

export const isActionCancelled = (
  log: IIssuingLog,
  issuingLogs?: IIssuingLog[]
) => {
  if (
    issuingLogs &&
    issuingLogs.length > 0 &&
    log &&
    issuingLogs.indexOf(log) >= 0
  ) {
    let index = issuingLogs.indexOf(log) - 1;
    let cancelAction;
    if (
      (log.action as IssuingRecordActionEnum) === IssuingRecordActionEnum.ISSUE
    ) {
      cancelAction = IssuingRecordActionEnum.REVOKE;
    } else if (
      (log.action as IssuingRecordActionEnum) === IssuingRecordActionEnum.REFUND
    ) {
      cancelAction = IssuingRecordActionEnum.RECHARGE;
    } else {
      return false;
    }
    while (index >= 0) {
      if (
        (issuingLogs[index].action as IssuingRecordActionEnum) ===
          (cancelAction as IssuingRecordActionEnum) &&
        issuingLogs[index].referenceId === log.id
      ) {
        return true;
      }
      index--;
    }
  }
  return false;
};

export const isActionCancelledNoSkip = (
  log: IIssuingLog,
  issuingLogs?: IIssuingLog[]
) => {
  if (
    issuingLogs &&
    issuingLogs.length > 0 &&
    log &&
    issuingLogs.indexOf(log) >= 0
  ) {
    let index = issuingLogs.indexOf(log) - 1;
    let cancelAction;
    if (
      (log.action as IssuingRecordActionEnum) === IssuingRecordActionEnum.ISSUE
    ) {
      cancelAction = IssuingRecordActionEnum.REVOKE;
    } else if (
      (log.action as IssuingRecordActionEnum) === IssuingRecordActionEnum.REFUND
    ) {
      cancelAction = IssuingRecordActionEnum.RECHARGE;
    } else {
      return false;
    }
    let otherActionCount = 0;
    while (index >= 0) {
      if (
        (issuingLogs[index].action as IssuingRecordActionEnum) ===
        (cancelAction as IssuingRecordActionEnum)
      ) {
        if (otherActionCount === 0) {
          return true;
        } else {
          otherActionCount--;
        }
      } else if (
        (issuingLogs[index].action as IssuingRecordActionEnum) ===
        (log.action as IssuingRecordActionEnum)
      ) {
        otherActionCount += 1;
      }
      index--;
    }
  }
  return false;
};

export const issuingRecordIdForCancelActionNoSkip = (
  cancelAction: IssuingRecordActionEnum,
  issuedStatus: IssuedStatusEnum,
  issuingLogs?: IIssuingLog[]
) => {
  if (
    ((issuedStatus as IssuedStatusEnum) === IssuedStatusEnum.PARTIAL ||
      (issuedStatus as IssuedStatusEnum) === IssuedStatusEnum.ISSUED) &&
    issuingLogs &&
    issuingLogs.length > 0
  ) {
    let targetAction: IssuingRecordActionEnum;
    if (
      (cancelAction as IssuingRecordActionEnum) ===
      IssuingRecordActionEnum.REVOKE
    ) {
      targetAction = IssuingRecordActionEnum.ISSUE;
    } else if (
      (cancelAction as IssuingRecordActionEnum) ===
      IssuingRecordActionEnum.RECHARGE
    ) {
      targetAction = IssuingRecordActionEnum.REFUND;
    } else {
      return '';
    }

    let lastActionIndex = 0;
    while (lastActionIndex < issuingLogs.length) {
      if (
        (issuingLogs[lastActionIndex].action as IssuingRecordActionEnum) ===
          (targetAction as IssuingRecordActionEnum) &&
        !isActionCancelledNoSkip(issuingLogs[lastActionIndex], issuingLogs) &&
        formatDate(new Date()) ===
          formatDate(new Date(issuingLogs[lastActionIndex].operatedAt))
      ) {
        break;
      } else {
        lastActionIndex++;
      }
    }
    if (lastActionIndex < issuingLogs.length) {
      return issuingLogs[lastActionIndex].id;
    }
  }
  return '';
};

export const formatIssuingRecordId = (id: string) =>
  id ? id.substr(id.length - 4) : '';
